import {
    articleKey, bonusReportKey, sectorAlertsKey, tradeAlertKey, webinarKey,
    articlesSyncKey, authorsSyncKey, bonusReportsSyncKey, categoriesSyncKey, productsSyncKey, sectorHistorySyncKey,
    sectorHistoryFullCheckDateKey, sectorHistoryFullCheckDueKey, sectorsSyncKey, simplePagesSyncKey, syncThrottlingKey, tickersSyncKey, tradeAlertsSyncKey, webinarsSyncKey
} from "@/types/products/product-keys";

/*******************************************************************************************************
 * DATABASE IDENTITY — FROZEN. Do not rename. Renaming an IndexedDB database does not migrate it; the
 * VERSION below is the only thing that changes when the cached shape changes. History: the app renamed
 * its database 34 times between 2026-02 and 2026-07 and paired each rename with a boot-time drop of the
 * previous name; that scheme isolated builds but cost a full re-download per rename. The IndexedDB version
 * gives the same isolation (a stale build cannot open a newer database) with an atomic, selective clear.
 * One deliberate rename since: `tmi-cache` → `sentient-cache` in v4.0.0 (Joel, 2026-10-26), shipped with
 * the first release of VERSION 9, whose clear already costs every client one full re-download.
 *******************************************************************************************************/
export const DATABASE_NAME = 'sentient-cache' as const;

/**
 * The IndexedDB version of `sentient-cache`. Bump it when a cached record's shape changes in a way that
 * makes previously cached rows wrong; `applyUpgrade` (cache-database.ts) clears the content stores
 * inside the upgrade transaction and every other open context reloads.
 *
 * 1 (2026-10-26) v4.0.0: new database `sentient-cache`, market data read from sentient_market_data; the old
 *                `tmi-cache` (which reached 9) is deleted by `purgeLegacyDatabases`, so every client starts empty
 */
export const CACHE_SCHEMA_VERSION: number = 1;

/** Every store this build declares. `applyUpgrade` deletes any other store it finds, so retiring one is a removal here plus a VERSION bump. */
export const STORE_NAMES = [
    'articles', 'authors', 'reports', 'categories', 'products',
    'sectors', 'history', 'pages', 'sync_state', 'tickers', 'alerts', 'webinars'
] as const;
export type StoreName = typeof STORE_NAMES[ number ];

/** Content every visitor sees identically. Kept across a change of signed-in user. */
export const PUBLIC_CONTENT_STORES: readonly ContentStoreName[] = [ 'authors', 'categories', 'products', 'sectors', 'pages', 'tickers' ];

/** Content the server filters by entitlement. Cleared whenever the cache changes hands. */
export const USER_SCOPED_CONTENT_STORES: readonly ContentStoreName[] = [ 'articles', 'reports', 'history', 'alerts', 'webinars' ];

export const CONTENT_STORES: readonly ContentStoreName[] = [ ...PUBLIC_CONTENT_STORES, ...USER_SCOPED_CONTENT_STORES ];

/** Sync bookkeeping: cooldowns, ETags, the owner stamp, the purge generation. Survives upgrades except for the keys below. */
export const STATE_STORE = 'sync_state' as const satisfies StoreName;
export type ContentStoreName = Exclude< StoreName, typeof STATE_STORE >;

/**
 * The record field each content store is keyed by, as its in-line IndexedDB keyPath (tmi-5q7). A store's keyPath
 * cannot be altered in place: changing one here needs a VERSION bump, and `applyUpgrade` deletes and recreates
 * every store whose keyPath disagrees. `createStore` only accepts the key field named here.
 */
export const CONTENT_KEY_PATHS = {
    articles: 'contentfulId', authors: 'id', reports: 'contentfulId', categories: 'id', products: 'id',
    sectors: 'contentfulId', history: 'dt', pages: 'id', tickers: 'id', alerts: 'contentfulId', webinars: 'contentfulId'
} as const satisfies Record< ContentStoreName, string >;

/**
 * UX roadmap #58: the product keys whose sync health the supervisor reports (MessageTypes.SYNC_STATUS): the member's
 * entitlement-filtered content, the part of the app that silently goes stale when syncs fail or the device is offline.
 */
export const SYNC_HEALTH_KEYS: readonly string[] = [ articleKey, bonusReportKey, tradeAlertKey, webinarKey, sectorAlertsKey ];

/** `sync_state` key holding when `productKey`'s content last synced cleanly (epoch ms). It describes the owner's rows. */
export function lastOkSyncKey( productKey: string ): string {
    return `lastOk:${ productKey }`;
}

/** Every cooldown/ETag key. Dropped inside an upgrade so the emptied stores refill on the next pass instead of after the previous throttle. */
export const CONTENT_COOLDOWN_KEYS: readonly string[] = [
    syncThrottlingKey,
    authorsSyncKey, categoriesSyncKey, productsSyncKey, sectorsSyncKey, simplePagesSyncKey,
    tickersSyncKey, `${ tickersSyncKey }:etag`,
    sectorHistorySyncKey, sectorHistoryFullCheckDateKey, sectorHistoryFullCheckDueKey,
    articlesSyncKey, bonusReportsSyncKey, tradeAlertsSyncKey, webinarsSyncKey,
    // The rows they date are cleared with the content stores.
    ...SYNC_HEALTH_KEYS.map( lastOkSyncKey )
];

/** Cooldowns covering the user-scoped stores only. Dropped when the cache changes hands. */
export const USER_SCOPED_COOLDOWN_KEYS: readonly string[] = [
    articlesSyncKey, bonusReportsSyncKey, tradeAlertsSyncKey, webinarsSyncKey, sectorHistorySyncKey,
    sectorHistoryFullCheckDateKey, sectorHistoryFullCheckDueKey,
    // Per owner: the next member must not be told their content was updated when the previous one's was.
    ...SYNC_HEALTH_KEYS.map( lastOkSyncKey )
];

/** `sync_state` key holding the id of the user whose entitlement-filtered content is cached. */
export const CACHE_OWNER_KEY = 'cache-owner' as const;

/**
 * Databases from earlier eras, deleted once per browser by `purgeLegacyDatabases`. `s` is the last
 * localforage-era database; `localforage` is the stray default database that `localforage.dropInstance`
 * created on every browser that ever ran the old purge. `tmi-cache` is the 2026-09..10 database, renamed
 * to `sentient-cache` in v4.0.0. The rest are the 2026-02..07 rename chain.
 */
export const LEGACY_DATABASE_NAMES: readonly string[] = [
    's', 'localforage', 'tmi-cache',
    'tmi', 'tmi-prime', 'tmi-se', 'tmi-sen', 'tmi-sent', 'tmi-senti', 'tmi-sentie', 'tmi-sentien',
    'sentient', 'sentient-', 'sentient-v', 'sentient-v1', 'sentient-v1.', 'sentient-v10',
    'sentient-v101', 'sentient-v1010', 'sentient-v10101', 'sentient-v101010', 'sentient-v1010101',
    'sentient-1010101', 'sentient-010101', 'sentient-10101', 'sentient-0101', 'sentient-101',
    'sentient-01', 'sentient-1', 'sentien', 'sentie', 'senti', 'sent', 'sen', 'se'
];
